import { Divider, Form } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { RevxDate } from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMoney } from 'components/RevxMoney';
import { TableColumnsType } from 'components/RevxTable';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { ShippingInfo } from 'domains/workspaceDetailDomain';
import {
  JobAdjustmentParams,
  JobAdjustmentPrintingCategoryEnum,
  JobAdjustmentPrintingItemViewer,
  JobAdjustmentShippingItemViewer,
  JobAdjustmentViewer,
  JobItem,
  JobParticipantEnum,
} from 'domains/workspaceDomain';
import { useRole, useUser } from 'hooks';
import { find } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addressTranslator } from 'utils/address';
import { TabsKeys } from '../index';
import {
  TotalRow,
  TotalRowKeysEnum,
  categorySelectOptions,
  totalRows,
} from './adjustmentTypes';
import JobAdjustmentContact from './components/JobAdjustmentContact';
import JobAdjustmentHeader from './components/JobAdjustmentHeader';
import JobAdjustmentPrinting from './components/JobAdjustmentPrinting';
import JobAdjustmentPrintingItems from './components/JobAdjustmentPrintingItems';
import JobAdjustmentShipping from './components/JobAdjustmentShipping';
import JobAdjustmentTotal from './components/JobAdjustmentTotal';
import {
  highlighterPrintingHandle,
  highlighterShippingHandle,
  highlighterTitleHandle,
} from './highlight';

type Props = {
  data?: JobAdjustmentViewer;
  originalData?: JobAdjustmentViewer;
  jobId: number;
  goBackWorkspace: () => void;
  updateJobAdjustmentData: (data: JobAdjustmentViewer) => void;
  jumpTab: (key: TabsKeys) => Promise<void>;
  refreshJobAdjustmentData: (cb?: () => void) => void;
  jobData?: JobItem;
};

export const HIGH_LIGHT_CLS = 'highlight';

export const matchCategoryLabel = (name: string) => {
  return find<{ value: string; label: string }>(categorySelectOptions, {
    value: name,
  })?.label;
};

const HistoryAdjustOriginal = (props: Props) => {
  const { data, jobId, updateJobAdjustmentData, jobData } = props;

  const [categoryListForm] = Form.useForm();
  const [totalListForm] = Form.useForm();
  const [shipAttachmentForm] = Form.useForm();
  const [shippingAdjustForm] = Form.useForm();
  const [categoryItem, setCategoryItem] =
    useState<JobAdjustmentPrintingItemViewer[]>();
  const [shippingItems, setShippingItems] =
    useState<JobAdjustmentShippingItemViewer[]>();
  const [feeLength, setFeeLength] = useState(0);
  const [totalRowsState, setTotalRowsState] = useState<TotalRow[]>(totalRows);
  const [shippingColumns, setShippingColumns] =
    useState<TableColumnsType<JobAdjustmentShippingItemViewer>>();
  const [adjustShippingPriceItem, setAdjustShippingPriceItem] =
    useState<JobAdjustmentPrintingItemViewer[]>();

  const { t } = useTranslation();
  const user = useUser();
  const { supplierRole, buyerRole } = useRole();
  categoryListForm.setFieldsValue({ categoryItem: categoryItem });
  shippingAdjustForm.setFieldsValue({ categoryItem: adjustShippingPriceItem });
  shipAttachmentForm.setFieldsValue({
    shipDateImpactAttachment: data?.shipDateImpactAttachment,
  });
  const { CUSTOMER, PRODUCER, PROXY } = JobParticipantEnum;
  const isOwnJob =
    jobData?.participants.includes(CUSTOMER) ||
    jobData?.participants.includes(PROXY);
  const isSupplierOwnJob = isOwnJob && jobData?.participants.includes(PRODUCER);
  const showCostCenterBySupplier =
    isSupplierOwnJob &&
    jobData?.participants.includes(PRODUCER) &&
    jobData?.participants.includes(PROXY);

  const initFeeRowData = () => {
    let initFeeLength = 0;
    if (data) {
      const newRow = totalRowsState.filter((row) => row.label);
      data.adjustTotal.fees.forEach(({ description, fee, serialNumber }) => {
        initFeeLength++;
        const key = genFeeKey(initFeeLength);
        newRow.splice(findTotalFeeRowIndex(undefined, newRow), 0, {
          label: undefined,
          key,
          description,
          fee,
          serialNumber,
        });
        totalListForm.setFieldsValue({ [key]: description });
      });
      setFeeLength(initFeeLength);
      setTotalRowsState(newRow);
    }
  };

  const findTotalFeeRowIndex = (
    otherFeeKey?: string,
    curTotalRows?: TotalRow[]
  ) => {
    const totalRows = curTotalRows || totalRowsState;
    return totalRows.findIndex(({ key }) =>
      otherFeeKey ? otherFeeKey === key : key === TotalRowKeysEnum.fee
    );
  };
  const genFeeKey = (len?: number) => {
    return TotalRowKeysEnum.fee + '_' + (len || feeLength);
  };

  useEffect(() => {
    if (!data) return;
    const { printingItems, shippingItems } = data;

    const filterPrinting = printingItems.filter(
      (i) =>
        i.category !== JobAdjustmentPrintingCategoryEnum.SHIPPING &&
        !i.copyFromOldVersion
    );
    const filterShipping = printingItems.filter(
      (i) =>
        i.category === JobAdjustmentPrintingCategoryEnum.SHIPPING &&
        !i.copyFromOldVersion
    );

    setCategoryItem(filterPrinting);
    setShippingItems(shippingItems);
    initFeeRowData();
    setAdjustShippingPriceItem(filterShipping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (shippingItems) {
      genShippingColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingItems, jobData]);

  const isHighCls = (is: boolean | undefined) => (is ? HIGH_LIGHT_CLS : '');
  const highlighterTitleCls = (dataIndex: string) =>
    isHighCls(highlighterTitleHandle({ ...props, user, dataIndex }));
  const highlighterShippingCls = (
    dataIndex: string,
    shipping: JobAdjustmentShippingItemViewer
  ) =>
    isHighCls(
      highlighterShippingHandle({ ...props, user, dataIndex, shipping })
    );
  const highlighterPrintingCls = (
    index: number,
    category: JobAdjustmentPrintingItemViewer
  ) =>
    isHighCls(highlighterPrintingHandle({ ...props, user, index, category }));

  const canEditStatusByRole = () => false;

  const genShippingColumns = () => {
    if (!data) return;
    const shippingTableColumn: TableColumnsType<JobAdjustmentShippingItemViewer> =
      [
        {
          title: 'Address',
          dataIndex: ['shippingInfo', 'toName'],
          render: (value, record, index) => {
            const { shippingInfo } = record;
            const renderInternalDistribution = (shippingInfo: ShippingInfo) => {
              if (shippingInfo?.internalDistribution) {
                const internalDistribution = shippingInfo.internalDistribution;
                const { quantity, distribution } = internalDistribution;
                return (
                  <div className="internal-distribution mt-10">
                    <label>
                      {t(
                        'workspace.detail.shippingBilling.internalDistribution'
                      )}
                      :
                    </label>
                    {internalDistribution && (
                      <p>
                        <span>
                          {quantity && <span>QTY: {quantity} | </span>}
                        </span>
                        <span>{distribution}</span>
                      </p>
                    )}
                  </div>
                );
              }
              return null;
            };
            return (
              <RevxFlexLayout
                wrap="wrap"
                className={highlighterShippingCls('toName', record)}
              >
                <div style={{ width: '100%' }}>{value}</div>
                <div>
                  {addressTranslator.dataToAddress(record.shippingInfo)}
                </div>
                {renderInternalDistribution(shippingInfo)}
              </RevxFlexLayout>
            );
          },
        },
        {
          title: 'Quantity',
          dataIndex: 'quantity',
        },
        {
          title: () => {
            return (
              <div>
                <p>Ready date/</p>
                <p>Ship date</p>
              </div>
            );
          },
          dataIndex: 'readyDate',
          width: 115,
          render: (value, record, index) => {
            return {
              children: (
                <div className={highlighterShippingCls('readyDate', record)}>
                  <div className="date-min-width">
                    <RevxDate dateStr={value} />
                  </div>
                </div>
              ),
              props: {
                rowSpan: index === 0 ? shippingItems?.length : 0,
              },
            };
          },
        },
        {
          title: () => {
            return (
              <div>
                <p>Shipping</p>
                <p>account</p>
              </div>
            );
          },
          dataIndex: ['shippingInfo', 'accountType'],
          render: (value, record, index) => {
            const {
              shippingInfo: { accountCompany },
            } = record;
            return (
              <div className={highlighterShippingCls('accountType', record)}>
                <div>{accountCompany}</div>
              </div>
            );
          },
        },
        {
          title: 'Shipping method',
          dataIndex: ['shippingInfo', 'carrierType'],
          render: (value, record, index) => {
            return (
              <div className={highlighterShippingCls('carrierService', record)}>
                <div>{record.shippingInfo.carrierService}</div>
              </div>
            );
          },
        },
        {
          title: 'Arrival date',
          dataIndex: ['deliveryDate'],
          width: 115,
          render: (value, record, index) => {
            return (
              <div className={highlighterShippingCls('deliveryDate', record)}>
                <div className="date-min-width">
                  <RevxDate dateStr={value} />
                </div>
              </div>
            );
          },
        },
        {
          title: () => {
            return (
              <div>
                <p>Requested</p>
                <p>delivery date</p>
              </div>
            );
          },
          width: 115,
          dataIndex: ['expectedDeliveryDate'],
          render: (value, record, index) => {
            return (
              <div
                className={highlighterShippingCls(
                  'expectedDeliveryDate',
                  record
                )}
              >
                <RevxDate dateStr={value} />
              </div>
            );
          },
        },
        {
          title: 'Price',
          dataIndex: ['price'],
          width: 100,
          render: (value, record, index) => {
            const {
              shippingInfo: { accountType },
              cost,
            } = record;
            const isSpecial =
              accountType === ShippingAccountTypeEnum.ORGANIZATION &&
              !(
                jobData?.participants.length === 1 &&
                jobData?.participants.includes(PRODUCER)
              );
            const price = isSpecial ? cost : value;
            const highlightClassName = highlighterShippingCls('price', record);
            const priceDom = (
              <div className={highlightClassName}>
                <RevxMoney value={price} />
                {isSpecial && '(billed separately)'}
              </div>
            );
            return priceDom;
          },
        },
      ];
    setShippingColumns(shippingTableColumn);
  };

  const runDebounceUpdateJobAdjustmentCostDataHandler = async (
    params: JobAdjustmentParams
  ) => {
    // Read-only: do nothing
  };

  if (!data) return <></>;
  const {
    jobName,
    orderId,
    costCenter,
    accountingLine,
    printing: { quantity, size, sizeMetric, sizePreset, sizePresetUnit, parts, orientation, services },
    promoterRole,
    adjustTotal,
    isReadjust,
    billingContact,
  } = data;

  const isOriginalPage = !promoterRole;
  const canShowEditButton = false;

  const generateJobAdjustmentCopy = async (
    category?: boolean,
    categoryType?: JobAdjustmentPrintingCategoryEnum
  ) => {
    // Read-only: do nothing
  };

  const addCategory = async (
    adjustmentId?: number,
    categoryType?: JobAdjustmentPrintingCategoryEnum
  ) => {
    return 0;
  };

  const addTempCategory = (
    categoryType?: JobAdjustmentPrintingCategoryEnum
  ) => {
    // Read-only: do nothing
  };

  const renderCategoryDom = (
    index: number,
    category: JobAdjustmentPrintingItemViewer,
    field: FormListFieldData,
    isShipping: boolean
  ) => {
    const defaultCategory = category;
    if (!defaultCategory) return <></>;
    const { id } = category;
    const highlightClassName = highlighterPrintingCls(index, category);
    switch (index) {
      case 0:
        if (isShipping) {
          return (
            <Form.Item
              {...field}
              name={[field.fieldKey, 'category']}
              rules={[{ required: true, message: 'Please select a category' }]}
              fieldKey={[field.fieldKey, 'category']}
              className={`td-category ${highlightClassName}`}
              data-item-row-id={id}
            >
              <div>Shipping</div>
            </Form.Item>
          );
        }
        return (
          <Form.Item
            {...field}
            name={[field.fieldKey, 'category']}
            rules={[{ required: true, message: 'Please select a category' }]}
            fieldKey={[field.fieldKey, 'category']}
            className={`td-category ${highlightClassName}`}
            data-item-row-id={id}
          >
            <div>{matchCategoryLabel(defaultCategory?.category)}</div>
          </Form.Item>
        );
      case 1:
        return (
          <Form.Item
            {...field}
            name={[field.fieldKey, 'itemName']}
            rules={[{ required: true }]}
            fieldKey={[field.fieldKey, 'itemName']}
            className={highlightClassName}
          >
            <div>
              <RevxTextWithTooltip
                text={defaultCategory?.itemName}
                maxWidth={400}
              />
            </div>
          </Form.Item>
        );
      case 2:
        return (
          <div>
            <div
              style={{
                paddingLeft: '6px',
              }}
            >
              <RevxMoney value={defaultCategory?.cost} />
            </div>
          </div>
        );
      case 3:
        return (
          <RevxFlexLayout className={`markup-wrap ${highlightClassName}`}>
            <RevxInputNumber
              disabled={true}
              value={defaultCategory?.markupPct}
              suffix={'%'}
              precision={2}
            />
            <Divider type="vertical" />
            <RevxInputNumber
              disabled={true}
              prefix={'$'}
              value={defaultCategory?.markup}
              precision={2}
            />
          </RevxFlexLayout>
        );
      case 4:
        return (
          <div>
            <div
              style={{
                fontWeight: 'bold',
                paddingLeft: '6px',
              }}
              className={highlightClassName}
            >
              <RevxMoney value={defaultCategory?.totalCost || 0} />
            </div>
          </div>
        );
      case 5:
        return <div className="delete-category"></div>;
      default:
        return <></>;
    }
  };

  const updateJobAdjustmentCostData = async (params: JobAdjustmentParams) => {
    // Read-only
  };

  return (
    <div className="job-adjust-original-wrap">
      <div className="job-adjust-original">
        <div className="job-adjust-content">
          {(!supplierRole || showCostCenterBySupplier) && (
            <JobAdjustmentHeader
              jobId={jobId}
              orderId={orderId}
              costCenter={costCenter}
              accountingLine={accountingLine}
              billingContact={billingContact}
              canShowEditButton={canShowEditButton}
              highlighterTitleCls={highlighterTitleCls}
              canEditStatusByRole={canEditStatusByRole}
              generateJobAdjustmentCopy={generateJobAdjustmentCopy}
              runDebounceUpdateJobAdjustmentCostDataHandler={
                runDebounceUpdateJobAdjustmentCostDataHandler
              }
              costDistribution={data.costDistribution}
              totalCharge={adjustTotal?.totalCharge}
              customerOrgId={jobData?.customerOrgId}
            />
          )}
          {!buyerRole && <JobAdjustmentContact data={data} />}
          <JobAdjustmentPrinting
            adjustTotal={adjustTotal}
            services={services}
            size={size}
            sizeMetric={sizeMetric}
            sizePreset={sizePreset}
            sizePresetUnit={sizePresetUnit}
            parts={parts}
            orientation={orientation}
            jobName={jobName}
            quantity={quantity}
            jobData={jobData}
          />
          <JobAdjustmentPrintingItems
            isReadjust={isReadjust}
            addCategory={addCategory}
            addTempCategory={addTempCategory}
            categoryItem={categoryItem}
            isOriginalPage={isOriginalPage}
            categoryListForm={categoryListForm}
            canShowEditButton={canShowEditButton}
            renderCategoryDom={renderCategoryDom}
            canEditStatusByRole={canEditStatusByRole}
            generateJobAdjustmentCopy={generateJobAdjustmentCopy}
          />
          <RevxFlexLayout
            className="title-bar"
            justify="space-between"
            align="center"
          >
            <div className="title-bar__name">
              <span>Shipping</span>
            </div>
            <div className="title-bar__price">
              <RevxMoney value={adjustTotal.shippingCost} />
            </div>
          </RevxFlexLayout>
          <div className="shipping-block">
            <JobAdjustmentShipping
              data={data}
              shippingItems={shippingItems}
              shippingColumns={shippingColumns}
              shipAttachmentForm={shipAttachmentForm}
              canEditStatusByRole={canEditStatusByRole}
              updateJobAdjustmentData={updateJobAdjustmentData}
              updateJobAdjustmentCostData={updateJobAdjustmentCostData}
              runDebounceUpdateJobAdjustmentCostDataHandler={
                runDebounceUpdateJobAdjustmentCostDataHandler
              }
            />
          </div>
          <JobAdjustmentPrintingItems
            isShipping={true}
            isReadjust={isReadjust}
            addCategory={addCategory}
            addTempCategory={addTempCategory}
            isOriginalPage={isOriginalPage}
            renderCategoryDom={renderCategoryDom}
            canShowEditButton={canShowEditButton}
            categoryListForm={shippingAdjustForm}
            categoryItem={adjustShippingPriceItem}
            canEditStatusByRole={canEditStatusByRole}
            generateJobAdjustmentCopy={generateJobAdjustmentCopy}
          />
          <JobAdjustmentTotal
            data={data}
            jobData={jobData}
            isReadjust={isReadjust}
            totalListForm={totalListForm}
            totalRowsState={totalRowsState}
            isOriginalPage={isOriginalPage}
            canEditStatusByRole={canEditStatusByRole}
            updateJobAdjustmentData={updateJobAdjustmentData}
            generateJobAdjustmentCopy={generateJobAdjustmentCopy}
            runDebounceUpdateJobAdjustmentCostDataHandler={
              runDebounceUpdateJobAdjustmentCostDataHandler
            }
          />
        </div>
      </div>
    </div>
  );
};

export default HistoryAdjustOriginal;
